import type { FilterData } from '@hotjar/feedback';
import { useCallback } from 'react';
import type { ReactionStyle } from '@hotjar/feedback/domain/Surveys/Questions/SurveyQuestion';
import { useSurveyFilters } from '@hotjar/feedback/hooks/useSurveyFilters';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { KeywordFilter } from './KeywordFilter';
import { PageUrlFilter } from './PageUrlFilter';
import { CompletionFilter } from './CompletionFilter';
import { RatingFilter } from './RatingFilter';
import { RecordingFilter } from './RecordingFilter';

export const SurveyResponseLocalFilters = ({
  hasKeywords,
  hasRatings,
  reactionStyle,
  isExternalLinkSurvey,
}: {
  hasKeywords?: boolean;
  hasRatings?: boolean;
  reactionStyle: ReactionStyle;
  isExternalLinkSurvey: boolean;
}): JSX.Element => {
  const { localFilters, setLocalFilters } = useSurveyFilters();
  const { t } = useTranslationWithPrefix('feedback:survey-response.filters');

  const onChange = useCallback(
    <T extends FilterData>(filter: T, value: T['value']) => {
      setLocalFilters(
        localFilters.map((f) => {
          if (f.id === filter.id) return { ...filter, value };
          return f;
        }),
      );
    },
    [localFilters, setLocalFilters],
  );

  const onRemove = useCallback(
    <T extends FilterData>(filter: T) => {
      setLocalFilters(
        localFilters.map((f) => {
          if (f.id === filter.id) return { ...filter, value: undefined };
          return f;
        }),
      );
    },
    [localFilters, setLocalFilters],
  );

  return (
    <>
      {localFilters.map((filter) => {
        switch (filter.type) {
          case 'url':
            return (
              <PageUrlFilter
                key={filter.id}
                filter={filter}
                onChange={onChange}
                onRemove={onRemove}
              />
            );
          case 'keyword':
            return (
              <KeywordFilter
                key={filter.id}
                filter={filter}
                onChange={onChange}
                onRemove={onRemove}
                isDisabled={hasKeywords === false}
                disabledReason={
                  hasKeywords === false ? t('keyword.disabled-reason-no-keywords') : undefined
                }
              />
            );
          case 'completion':
            return (
              <CompletionFilter
                key={filter.id}
                filter={filter}
                onChange={onChange}
                onRemove={onRemove}
              />
            );
          case 'recording':
            return (
              <RecordingFilter
                key={filter.id}
                filter={filter}
                onChange={onChange}
                onRemove={onRemove}
                isDisabled={isExternalLinkSurvey}
                disabledReason={
                  isExternalLinkSurvey ? t('recording.disabled-reason-no-link-survey') : undefined
                }
              />
            );
          case 'reaction':
            return (
              <RatingFilter
                key={filter.id}
                filter={filter}
                onChange={onChange}
                onRemove={onRemove}
                emotionStyle={reactionStyle}
                isDisabled={hasRatings === false}
                disabledReason={
                  hasRatings === false ? t('rating.disabled-reason-no-ratings') : undefined
                }
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
};
